import { ServiceHandler } from '../../utils';
import { standardQuerySerializer } from '../serializers';
import type {
  ChatWithDocumentApiResponse,
  ChatWithDocumentParams,
  ExtractRagFieldApiResponse,
  ExtractRagFieldParams,
  GetChatHistoryParams,
  GetChatHistoryResponse,
  GetDocumentContentParams,
} from './document-rag.api.types';

/**
 * Extract a specific field value from submission documents using RAG
 *
 * @example
 * ```typescript
 * const result = await extractRagField({
 *   params: {
 *     field: 'companyName',
 *     messageId: 'msg-123',
 *     submissionId: 'sub-456',
 *     maxResults: 5
 *   }
 * });
 * ```
 */
export const extractRagField = ServiceHandler<
  { params: ExtractRagFieldParams },
  ExtractRagFieldApiResponse
>({
  url: '/api/docs/v1/rag/extract',
  method: 'GET',
  paramsSerializer: (params) => {
    return standardQuerySerializer(params);
  },
});

/**
 * Chat with documents using RAG to answer questions about submission documents
 *
 * @example
 * ```typescript
 * const result = await chatWithDocument({
 *   params: {
 *     prompt: 'What is the company revenue?',
 *     messageId: 'msg-123',
 *     submissionId: 'sub-456',
 *     conversationId: 'conv-789' // optional
 *   }
 * });
 * ```
 */
export const chatWithDocument = ServiceHandler<
  { params: ChatWithDocumentParams },
  ChatWithDocumentApiResponse
>({
  url: '/api/docs/v1/rag/chatWithDocument',
  method: 'GET',
  paramsSerializer: (params) => {
    return standardQuerySerializer(params);
  },
});

/**
 * Get chat history for a submission using RAG
 *
 * @example
 * ```typescript
 * const result = await getChatHistory({
 *   params: {
 *     submissionId: 'sub-456'
 *   }
 * });
 * ```
 */
export const getChatHistory = ServiceHandler<
  { params: GetChatHistoryParams },
  GetChatHistoryResponse
>({
  url: '/api/docs/v1/rag/getChatHistory',
  method: 'GET',
  paramsSerializer: (params) => {
    return standardQuerySerializer(params);
  },
});

/**
 * Get document content from S3 using S3 URI
 *
 * @example
 * ```typescript
 * const result = await getDocumentContent({
 *   params: {
 *     s3Uri: 's3://bucket-name/path/to/document.pdf'
 *   }
 * });
 * ```
 */
export const getDocumentContent = ServiceHandler<
  { params: GetDocumentContentParams },
  Blob
>({
  url: '/api/docs/v1/rag/get_content',
  method: 'GET',
  paramsSerializer: (params) => {
    return standardQuerySerializer(params);
  },
  responseType: 'blob',
});
